import moment from "moment-timezone";
import PropTypes from "prop-types";
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

/* SVG Files */
import deleteWhite from "../../../assets/img/icons/delete_white.svg";
import Info from "../../../assets/img/icons/info.svg";
import CommonTooltip from "../../../components/Tooltip/CommonTooltip";
import { WhiteButton } from "../../../styledComponents/buttons";
import { CommonGrid, CommonText } from "../../../styledComponents/common";
import { ActivityTextSection } from "../../../styledComponents/members";
import {
  CalendarContainer,
  DeleteButton,
  InfoButton,
  ScrollToBottomButton,
  TimeSlot,
  TimeSlotCard,
  TimeSlotInnerCard,
  TimeSlotSmallInfo,
  TimesheetTooltipContainer,
  UpperTotalHour,
} from "../../../styledComponents/timesheet";
import { toHHMMSS } from "../../../utils/helper";
import { TimesheetDetailsPopup } from "./TimesheetDaily";
import {
  getDuration,
  getDurationInMinutes,
  getHours,
  getZeroHourTime,
} from "./helperFunctions";
import { timeList } from "./timeList";

const ContentTitle = ({ selectedOrganization }) => {
  // Dynamic labels based on organization type (default to "operation" if not defined)
  const orgType = selectedOrganization?.type || "operation";

  return (
    <TimeSlotInnerCard>
      <ActivityTextSection style={{ textAlign: "left" }}>
        <CommonText $label fontSize="12.5px">
          {orgType === "service" ? "Job" : "Project & Task"}  
        </CommonText>
      </ActivityTextSection>
      <ActivityTextSection>
        <CommonText $label fontSize="12.5px">
          Start Time
        </CommonText>
      </ActivityTextSection>
      <ActivityTextSection>
        <CommonText $label fontSize="12.5px">
          End Time
        </CommonText>
      </ActivityTextSection>
      <ActivityTextSection style={{ textAlign: "right" }}>
        <CommonText $label fontSize="12.5px">
          Total Time
        </CommonText>
      </ActivityTextSection>
    </TimeSlotInnerCard>
  );
};

export const TimeSlotContent = memo(
  ({
    timesheetList,
    filteredTimeList, // not used yet, maybe remove if unnecessary
    infoPopup,
    setInfoPopup,
    toggleInfoPopup,
    selectIndex,
    idleTimePopup,
    setIdleTimePopup,
    toggleIdleTimePopup,
    filterSelectedDate,
    onHoverChange,
    toggleManualTimeModal,
    toggleDeleteTimeModal,
    toggleIdleTimeModal,
    totalIdleTimesheet,
    selectedOrganization,
    durations,
  }) => {
    // Dynamic labels based on organization type (default to "operation" if not defined)
    const orgType = selectedOrganization?.type || "operation";

    const [showScrollButton, setShowScrollButton] = useState(false);
    const [isAnimatingIn, setIsAnimatingIn] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);
    const containerRef = useRef(null);
    const lastSlotRef = useRef(null);
    const debounceRef = useRef(null);

    const checkLastSlotVisibility = useCallback(() => {
      if (
        lastSlotRef.current &&
        containerRef.current &&
        timesheetList?.length > 0
      ) {
        const lastSlotRect = lastSlotRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        const isLastSlotHidden = lastSlotRect.bottom > viewportHeight;

        setShowScrollButton(current => {
          if (isLastSlotHidden && !current) return true;
          if (!isLastSlotHidden && current) return false;
          return current;
        });
      } else if (timesheetList?.length === 0) {
        setShowScrollButton(false);
      }
    }, [timesheetList?.length]);

    useEffect(() => {
      if (showScrollButton) {
        setShouldRender(true);
        setIsAnimatingIn(false);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsAnimatingIn(true);
          });
        });
      } else {
        setIsAnimatingIn(false);
        setTimeout(() => setShouldRender(false), 300);
      }
    }, [showScrollButton]);

    const scrollToBottom = () => {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: "smooth",
      });
    };

    useEffect(() => {
      if (!timesheetList?.length) return;

      const timers = [];
      timers.push(setTimeout(checkLastSlotVisibility, 100));
      timers.push(setTimeout(checkLastSlotVisibility, 300));

      const handleScroll = () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(checkLastSlotVisibility, 150);
      };

      const handleResize = () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(checkLastSlotVisibility, 200);
      };

      window.addEventListener("scroll", handleScroll, { passive: true });
      window.addEventListener("resize", handleResize, { passive: true });

      return () => {
        timers.forEach(t => clearTimeout(t));
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleResize);
        if (debounceRef.current) clearTimeout(debounceRef.current);
      };
    }, [timesheetList?.length, checkLastSlotVisibility]);

    return (
      <>
        <CalendarContainer ref={containerRef}>
          <div></div>
          <CommonGrid
            justifyContent="start"
            gap="0 40px"
            columns="auto auto auto"
          >
            <ActivityTextSection>
              <CommonText style={{ textAlign: "center" }}>
                {moment(filterSelectedDate).format("ddd")}
              </CommonText>
              <WhiteButton
                cursor="auto"
                height="45px"
                width="45px"
                padding="0"
                borderColor="#20bead"
                hoverBorderColor="#20bead"
              >
                {moment(filterSelectedDate).format("DD")}
              </WhiteButton>
            </ActivityTextSection>
            <UpperTotalHour>
              <CommonText $label>Total Time</CommonText>
              <CommonText title>
                {durations && durations.length > 0
                  ? getHours(
                      toHHMMSS(
                        durations
                          .slice(1)
                          .reduce((p, c) => p + c, durations[0]),
                      ),
                    )
                  : "0 hour 00 minute"}
              </CommonText>
            </UpperTotalHour>
          </CommonGrid>
        </CalendarContainer>

        <CalendarContainer style={{ marginTop: "60px" }}>
          {timeList.map(
            (time, index) =>
              timesheetList &&
              timesheetList.length > 0 &&
              index > moment(timesheetList[0].start_time).hours() - 1 &&
              index <
                moment(
                  timesheetList[timesheetList.length - 1].end_time,
                ).hours() +
                  1 && (
                <Fragment key={index}>
                  <CommonText margin="0">{time.time}</CommonText>
                  <TimeSlot
                    index={index}
                    startPosition={moment(timesheetList[0].start_time).hours()}
                  />
                </Fragment>
              ),
          )}

          {timesheetList &&
            timesheetList.length > 0 &&
            timesheetList.map((card, index) => {
              const durationInMinutes = getDurationInMinutes(card.duration);
              const isLast = index === timesheetList.length - 1;

              return (
                <Fragment key={index}>
                  {infoPopup === index && (
                    <TimesheetDetailsPopup
                      index={index}
                      data={card}
                      startPosition={moment(
                        timesheetList[0].start_time,
                      ).hours()}
                      getDuration={getDuration}
                      toggleDeleteTimeModal={toggleDeleteTimeModal}
                      setInfoPopup={setInfoPopup}
                      selectedOrganization={selectedOrganization}
                    />
                  )}

                  {/* Activity Box Title */}
                  {index === 0 &&
                    timesheetList.some(
                      c => getDurationInMinutes(c.duration) > 20,
                    ) && (
                      <TimeSlotCard
                        height={20}
                        index={index}
                        left={120}
                        bgColor="none"
                        borderColor="none"
                        padding="0 40px 0 47px"
                        style={{ top: "-30px" }}
                      >
                        <ContentTitle
                          selectedOrganization={selectedOrganization}
                        />
                      </TimeSlotCard>
                    )}

                  <TimeSlotCard
                    ref={isLast ? lastSlotRef : null}
                    top={card.top}
                    height={card.height}
                    index={index}
                    startPosition={moment(timesheetList[0].start_time).hours()}
                    small={durationInMinutes < 20}
                    clickable={durationInMinutes <= 40}
                    onMouseEnter={() => onHoverChange(index)}
                    onMouseLeave={() => onHoverChange(-1)}
                    onClick={() =>
                      durationInMinutes <= 40
                        ? toggleInfoPopup(index)
                        : undefined
                    }
                  >
                    {durationInMinutes < 20 ? (
                      <TimeSlotSmallInfo
                        index={index}
                        top={parseInt(card.height / 2)}
                        onClick={() => toggleInfoPopup(index)}
                      >
                        <img src={Info} alt="" />
                      </TimeSlotSmallInfo>
                    ) : (
                      <TimeSlotInnerCard>
                        <ActivityTextSection>
                          {orgType !== "service" ? (
                            <>
                              {card.work_order ? (
                                <CommonText
                                  name
                                  title
                                  style={{ textAlign: "left" }}
                                >
                                  {card.work_order.name}
                                </CommonText>
                              ) : (
                                <CommonText
                                  name
                                  title
                                  style={{ textAlign: "left", opacity: "50%" }}
                                >
                                  (No project)
                                </CommonText>
                              )}

                              {durationInMinutes > 40 && (
                                <>
                                  {card.job ? (
                                    <CommonText
                                      fontSize="12px"
                                      title
                                      style={{ textAlign: "left" }}
                                    >
                                      {card.job.name}
                                    </CommonText>
                                  ) : (
                                    <CommonText
                                      fontSize="12px"
                                      title
                                      style={{ textAlign: "left", opacity: "50%" }}
                                    >
                                      (No task)
                                    </CommonText>
                                  )}
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              {card.job ? (
                                <CommonText
                                  name
                                  title
                                  style={{ textAlign: "left" }}
                                >
                                  {card.job.name}
                                </CommonText>
                              ) : (
                                <CommonText
                                  name
                                  title
                                  style={{ textAlign: "left", opacity: "50%" }}
                                >
                                  (No job)
                                </CommonText>
                              )}
                            </>
                          )}
                        </ActivityTextSection>

                        <ActivityTextSection>
                          <CommonText name title>
                            {card.start_time.format("h:mm a")}
                          </CommonText>
                        </ActivityTextSection>

                        <ActivityTextSection>
                          <CommonText name title>
                            {card.end_time.format("h:mm a")}
                          </CommonText>
                        </ActivityTextSection>

                        <ActivityTextSection style={{ textAlign: "right" }}>
                          <CommonText name title>
                            {getZeroHourTime(card.duration)}
                          </CommonText>
                        </ActivityTextSection>

                        {selectIndex === index && (
                          <>
                            <DeleteButton
                              onClick={() => toggleDeleteTimeModal(card)}
                            >
                              <img
                                src={deleteWhite}
                                style={{ width: "24px", height: "24px" }}
                                alt="delete"
                              />
                              <TimesheetTooltipContainer>
                                <CommonTooltip
                                  tooltipText="Delete"
                                  left="-50px"
                                  top="-46px"
                                  width="85px"
                                  height="25px"
                                />
                              </TimesheetTooltipContainer>
                            </DeleteButton>
                            {durationInMinutes <= 40 &&
                              durationInMinutes >= 20 && (
                                <InfoButton
                                  index={index}
                                  onClick={() => toggleInfoPopup(index)}
                                >
                                  <img src={Info} alt="" />
                                  <TimesheetTooltipContainer>
                                    <CommonTooltip
                                      tooltipText="Details"
                                      left="-50px"
                                      top="-46px"
                                      width="85px"
                                      height="25px"
                                    />
                                  </TimesheetTooltipContainer>
                                </InfoButton>
                              )}
                          </>
                        )}
                      </TimeSlotInnerCard>
                    )}
                  </TimeSlotCard>
                </Fragment>
              );
            })}
        </CalendarContainer>

        {(() => {
          if (!shouldRender && !isAnimatingIn) return null;
          if (!timesheetList?.length) return null;

          return (
            <ScrollToBottomButton
              $isVisible={isAnimatingIn}
              onClick={scrollToBottom}
            >
              <span>Scroll for more</span>
            </ScrollToBottomButton>
          );
        })()}
      </>
    );
  },
);
